import {WonderlandEngine} from '../engine.js';
import {Prefab} from '../prefab.js';
import {Scene} from '../scene.js';
import {FirstConstructorParam} from '../types.js';

/**
 * Resource type. @hidden
 *
 * Must be kept in sync with Scene.cpp.
 */
export enum ResourceType {
    None = 0,
    Material,
    Mesh,
    Image,
    MorphTarget,
    Font,
    ParticleEffect,
    ProbeVolumeScenario,
    AudioClip,
    Pipeline,

    Count,
}

/** Interface for a resource class */
type ResourceConstructor<T extends SceneResource | Resource> = {
    new (host: FirstConstructorParam<T>, index: number): T;
};

/**
 * Create a proxy throwing destroyed errors upon access.
 *
 * @param type The type to display upon error
 * @returns The proxy instance
 */
function createDestroyedProxy<T extends SceneResource | Resource>(
    host: FirstConstructorParam<T>,
    type: ResourceConstructor<T>
) {
    return new Proxy(
        {},
        {
            get(_, param: string) {
                if (param === 'isDestroyed') return true;
                throw new Error(
                    `Cannot read '${param}' of destroyed '${type.name}' resource from ${host}`
                );
            },
            set(_, param: string) {
                throw new Error(
                    `Cannot write '${param}' of destroyed '${type.name}' resource from ${host}`
                );
            },
        }
    );
}

/**
 * Base class for engine resources, such as:
 * - {@link Texture}
 * - {@link Mesh}
 * - {@link Material}
 *
 * @since 1.2.0
 */
export abstract class Resource {
    /** Resource type. @hidden */
    static getResourceType(): ResourceType | null {
        return null;
    }

    /** Relative index in the host. @hidden */
    readonly _index: number = -1;
    /** For compatibility with SceneResource. @hidden */
    readonly _id: number = -1;
    /** @hidden */
    private readonly _engine: WonderlandEngine;

    constructor(engine: WonderlandEngine, index: number) {
        this._engine = engine;
        this._index = index;
        this._id = index;
    }

    /** Hosting engine instance. */
    get engine() {
        return this._engine;
    }

    /** Index of this resource in the {@link Scene}'s manager. */
    get index() {
        return this._index;
    }

    /**
     * Scene with which the resource was originally loaded.
     *
     * @note If the scene has since been destroyed, returns `null`.
     *
     * #### Example
     *
     * ```ts
     * const prefab = await engine.loadPrefab('Prefab.bin');
     *
     * const meshA = engine.meshes.create({vertexCount: 1});
     * console.log(meshA.origin === prefab); // false
     *
     * const components = prefab.getComponents('mesh');
     * for (const component of components) {
     *     const original = component.mesh.origin;
     *     console.log(prefab === original); // true
     * }
     * ```
     *
     * @since 1.5.0
     */
    get origin(): Prefab | null {
        const type = (this.constructor as typeof Resource).getResourceType();
        if (type === null) return null;

        const index = this.engine.wasm._wl_resource_originScene(type, this._index);
        return index >= 0 ? this.engine._scenes[index] : null;
    }

    /**
     * Checks equality by comparing ids and **not** the JavaScript reference.
     *
     * @deprecated Use JavaScript reference comparison instead:
     *
     * ```js
     * const meshA = engine.meshes.create({vertexCount: 1});
     * const meshB = engine.meshes.create({vertexCount: 1});
     * const meshC = meshB;
     * console.log(meshA === meshB); // false
     * console.log(meshA === meshA); // true
     * console.log(meshB === meshC); // true
     * ```
     */
    equals(other: this | undefined | null): boolean {
        /** @todo(2.0.0): Remove */
        if (!other) return false;
        return this._index === other._index;
    }

    /**
     * `true` if the object is destroyed, `false` otherwise.
     *
     * If {@link WonderlandEngine.erasePrototypeOnDestroy} is `true`,
     * reading a class attribute / method will throw.
     */
    get isDestroyed() {
        /** @todo: Refactor to use _wl_resource_destroyed */
        return this._index <= 0;
    }
}

/**
 * Base class for scene resources, such as:
 *  * - {@link Texture}
 * - {@link Mesh}
 * - {@link Material}
 * - {@link Skin}
 * - {@link Animation}
 *
 * @since 1.2.0
 */
export abstract class SceneResource {
    /** @hidden */
    static _pack(scene: number, index: number) {
        return (scene << 22) | index;
    }
    static _unpackSceneIndex(packed: number) {
        return packed >> 22;
    }
    static _unpackId(packed: number) {
        return packed & 0x003fffff;
    }

    /** Relative index in the host. @hidden */
    readonly _index: number = -1;
    /** For compatibility with SceneResource. @hidden */
    readonly _id: number = -1;
    /** @hidden */
    protected readonly _scene: Prefab;

    constructor(scene: Prefab, index: number) {
        this._scene = scene;
        this._index = index;
        this._id = SceneResource._pack(scene._index, index);
    }

    /**
     * Checks equality by comparing ids and **not** the JavaScript reference.
     *
     * @deprecated Use JavaScript reference comparison instead:
     *
     * ```js
     * const meshA = engine.meshes.create({vertexCount: 1});
     * const meshB = engine.meshes.create({vertexCount: 1});
     * const meshC = meshB;
     * console.log(meshA === meshB); // false
     * console.log(meshA === meshA); // true
     * console.log(meshB === meshC); // true
     * ```
     */
    equals(other: this | undefined | null): boolean {
        if (!other) return false;
        return this._id === other._id;
    }

    /** Hosting instance. */
    get scene() {
        return this._scene;
    }

    /** Hosting engine instance. */
    get engine() {
        return this._scene.engine;
    }

    /** Index of this resource in the {@link Scene}'s manager. */
    get index() {
        return this._index;
    }

    /**
     * `true` if the object is destroyed, `false` otherwise.
     *
     * If {@link WonderlandEngine.erasePrototypeOnDestroy} is `true`,
     * reading a class attribute / method will throw.
     */
    get isDestroyed() {
        return this._id <= 0;
    }
}

/**
 * Manager for resources.
 *
 * Resources are accessed via the engine they belong to.
 *
 * @see {@link WonderlandEngine.textures}, {@link WonderlandEngine.meshes},
 * and {@link WonderlandEngine.materials}.
 *
 * @since 1.2.0
 */
export class ResourceManager<T extends SceneResource | Resource> {
    /** @hidden */
    protected readonly _host: FirstConstructorParam<T>;
    /** Cache. @hidden */
    protected readonly _cache: (T | null)[] = [];

    /** Resource class. @hidden */
    private readonly _template: ResourceConstructor<T>;

    /** Destructor proxy, used if {@link WonderlandEngine.erasePrototypeOnDestroy} is `true`. @hidden */
    private _destructor: {} | null = null;

    private readonly _engine: WonderlandEngine;

    /**
     * Create a new manager
     *
     * @param host The host containing the managed resources.
     * @param Class The class to instantiate when wrapping an index.
     *
     * @hidden
     */
    constructor(host: FirstConstructorParam<T>, Class: ResourceConstructor<T>) {
        this._host = host;
        this._template = Class;
        this._engine = (host as Prefab).engine ?? host;
    }

    /**
     * Wrap the index into a resource instance.
     *
     * @note The index is relative to the host, i.e., doesn't pack the host index (if any).
     *
     * @param index The resource index.
     * @returns
     */
    wrap(index: number) {
        if (index <= 0) return null;
        const resource =
            this._cache[index] ??
            (this._cache[index] = new this._template(this._host, index));
        return resource;
    }

    /**
     * Return all resources loaded from a given scene
     *
     * @note Resources created at runtime are retrieved using:
     *
     * ```ts
     * // All textures loaded from the current active scene
     * const mainTextures = engine.textures.loadedFromScene(engine.scene);
     *
     * const prefab = await engine.loadPrefab('Prefab.bin');
     * // All textures loaded from the prefab
     * const prefabTextures = engine.textures.loadedFromScene(prefab);
     * ```
     *
     * @param prefab The prefab
     * @returns Array of resources loaded from the given prefab.
     *
     * @since 1.5.0
     */
    loadedFromScene(prefab: Prefab): T[] {
        const type = this._resourceType;
        if (type === null) return [];

        const result: T[] = [];
        const wasm = this.engine.wasm;
        const count = wasm._wl_resource_count(type);
        for (let i = 1; i < count; ++i) {
            if (!!wasm._wl_resource_destroyed(type, i)) continue;
            const resource = this.wrap(i)! as Resource;
            if (resource.origin !== prefab) continue;
            result.push(resource as T);
        }
        return result;
    }

    /**
     * Retrieves non-destroyed resources.
     *
     * To retrieve resources loaded by a specific scene, use
     * {@link ResourceManager.loadedFromScene}.
     *
     * #### Example
     *
     * ```ts
     * // All texture resources currently alive
     * const textures = engine.textures.all();
     * ...
     * await engine.textures.create('my.png');
     * // `textures` now includes 'my.png'
     * const textures = engine.textures.all();
     * ```
     *
     * @returns Array of all resources.
     *
     * @since 1.5.0
     */
    all(): T[] {
        const type = this._resourceType;
        if (type === null) return [];

        const wasm = this.engine.wasm;
        const count = wasm._wl_resource_count(type);
        const result: T[] = [];

        for (let i = 1; i < count; ++i) {
            if (!!wasm._wl_resource_destroyed(type, i)) continue;
            result.push(this.wrap(i)!);
        }
        return result;
    }

    /**
     * Retrieve the resource at the given index.
     *
     * @note The index is relative to the host, i.e., doesn't pack the host index.
     */
    get(index: number): T | null {
        return this._cache[index] ?? null;
    }

    /** Number of textures allocated in the manager. */
    get allocatedCount() {
        /** @todo(2.0.0): Remove, this is not useful */
        return this._cache.length;
    }

    /**
     * Number of resources in the manager.
     *
     * @note For performance reasons, avoid calling this method when possible.
     */
    get count() {
        /** @todo(2.0.0): Remove, this is not useful */
        let count = 0;
        for (const res of this._cache) {
            if (res && res.index >= 0) ++count;
        }
        return count;
    }

    /** Hosting engine instance. */
    get engine() {
        return this._engine;
    }

    /** @hidden */
    get _resourceType(): ResourceType | null {
        const ResourceClass = this._template as unknown as typeof Resource;
        return ResourceClass.getResourceType ? ResourceClass.getResourceType() : null;
    }

    /**
     * Destroy the instance.
     *
     * @note This method takes care of the prototype destruction.
     *
     * @hidden
     */
    _destroy(instance: T) {
        const index = instance.index;
        (instance._index as number) = -1;
        (instance._id as number) = -1;
        this._cache[index] = null;

        if (!this.engine.erasePrototypeOnDestroy) return;

        if (!this._destructor)
            this._destructor = createDestroyedProxy(this._host, this._template);
        Object.setPrototypeOf(instance, this._destructor);
    }

    /**
     * Mark all instances as destroyed.
     *
     * @hidden
     */
    _clear() {
        if (!this.engine.erasePrototypeOnDestroy) return;
        for (let i = 0; i < this._cache.length; ++i) {
            const instance = this._cache[i];
            if (instance) this._destroy(instance);
        }
        this._cache.length = 0;
    }
}
